import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { NotificationDefaults } from "@/lib/types";

const KEY = ["admin", "notification-defaults"];

// Admin-only: org-wide notification defaults + obligatory locks. Optimistic on
// save (mirrors useNotificationPrefs) so rapid toggles don't flip back.
export function useNotificationDefaults() {
  const qc = useQueryClient();
  const query = useQuery<NotificationDefaults>({
    queryKey: KEY,
    queryFn: () => api.getNotificationDefaults(),
    staleTime: 60_000,
  });
  const save = useMutation({
    mutationFn: (d: NotificationDefaults) =>
      api.updateNotificationDefaults({ matrix: d.matrix, locked: d.locked }),
    onMutate: (d: NotificationDefaults) => {
      const prev = qc.getQueryData<NotificationDefaults>(KEY);
      qc.setQueryData(KEY, d);
      return { prev };
    },
    onError: (_e, _d, ctx) => {
      if (ctx?.prev) qc.setQueryData(KEY, ctx.prev);
    },
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      // A default/lock change alters what every user inherits — refresh the
      // user's own prefs view so a locked cell greys immediately.
      qc.invalidateQueries({ queryKey: ["me", "notification-prefs"] });
    },
  });
  return { query, save };
}
